import { EligibleProduct } from "@/sales-tools";
import { SaleProgress } from "@/sales-tools/home-product/types";
import { mapPartnerToDisplayName } from "@/sales-tools/offers-view/CustomerView";

export function shouldShowVerizonProduct(params: {
    product: EligibleProduct;
    isVerizonCareEnabled: boolean;
    pilots: string[];
    products: EligibleProduct[];
    client: string;
}) {
    const { product, isVerizonCareEnabled, pilots, products, client } = params;

    const isVerizonCareUser = pilots.includes("care") && isVerizonCareEnabled;
    const isVZNAALSuccessful = products.find((p) => p.productSku === "VZN_AAL")?.saleProgress === SaleProgress.Success;

    // Special case for VERIZON_OFR client
    if (client === "VERIZON_OFR" && product.productSku === "VZN_VMPMD") {
        return true;
    }

    // Special case for VZN_VMPMD product
    if (product.productSku === "VZN_VMPMD" && client !== "VERIZON_IBC") {
        return isVerizonCareUser || isVZNAALSuccessful;
    }

    // All other products should be shown
    return true;
}

export function shouldRenderProduct(params: {
    product: EligibleProduct;
    partner: string;
    isVerizonCareEnabled: boolean;
    pilots: string[];
    products: EligibleProduct[];
    client: string;
}) {
    const { product, partner } = params;

    if (partner === "att") {
        return product.productSku !== "ATT_PA1_STATIC" && product.productSku !== "ATT_PA4_STATIC";
    }

    return shouldShowVerizonProduct(params);
}

export function getVerizonCareProductInfo(params: {
    partner: string;
    pilots: string[];
    isVerizonCareEnabled: boolean;
}) {
    const { partner, pilots, isVerizonCareEnabled } = params;

    const isVerizonCareUser = pilots.includes("care") && isVerizonCareEnabled;
    return isVerizonCareUser ? "verizonCare" : partner;
}

export function formatIneligibleProductDisplay(params: { product: EligibleProduct; partner: string }) {
    const { product, partner } = params;

    const displayName = product.productInfo?.["en-US"].displayName ?? product.productSku;
    return `${mapPartnerToDisplayName(partner)} ${displayName}`;
}

export function getProductCardVariant({
    product,
    isChat,
}: {
    product: EligibleProduct;
    isChat: boolean;
}): "full" | "compact" {
    if (isChat) return "full";

    return product.productInfo?.["en-US"].cardVariant === "full" ? "full" : "compact";
}

export type ProductCategory = {
    primary: EligibleProduct[];
    secondary: EligibleProduct[];
    ineligible: EligibleProduct[];
};

export function categorizeProductsByType(params: {
    products: EligibleProduct[];
    partner: string;
    isVerizonCareEnabled: boolean;
    pilots: string[];
    client: string;
}) {
    const { products, partner, isVerizonCareEnabled, pilots, client } = params;

    const eligibleProducts = products.filter((product) => product.status === "ELIGIBLE" && !!product.productInfo);
    const ineligibleProducts = products.filter((product) => product.status !== "ELIGIBLE" && !!product);

    const primary: EligibleProduct[] = [];
    const secondary: EligibleProduct[] = [];
    const ineligible: EligibleProduct[] = [];
    eligibleProducts.forEach((product) => {
        const override = isRecommendedOverride(products, client, product.productSku);

        if (override !== undefined) {
            if (override) {
                const overriddenProduct = {
                    ...product,
                    productInfo: {
                        ...product.productInfo,
                        "en-US": {
                            ...product.productInfo?.["en-US"],
                            cardVariant: "full",
                            isRecommended: true,
                        },
                    },
                };
                primary.push(overriddenProduct as EligibleProduct);
            } else {
                const overriddenProduct = {
                    ...product,
                    productInfo: {
                        ...product.productInfo,
                        "en-US": {
                            ...product.productInfo?.["en-US"],
                            cardVariant: "compact",
                            isRecommended: false,
                        },
                    },
                };
                secondary.push(overriddenProduct as EligibleProduct);
            }
        } else {
            if (
                shouldRenderProduct({ product, partner, isVerizonCareEnabled, pilots, products, client }) &&
                product.productInfo?.["en-US"].cardVariant === "full"
            ) {
                primary.push(product);
            } else if (shouldRenderProduct({ product, partner, isVerizonCareEnabled, pilots, products, client })) {
                secondary.push(product);
            }
        }
    });

    ineligibleProducts.forEach((product) => {
        if (shouldRenderProduct({ product, partner, isVerizonCareEnabled, pilots, products, client })) {
            ineligible.push(product);
        }
    });

    return { primary, secondary, ineligible };
}

export function isRecommendedOverride(products: EligibleProduct[], client?: string, productSku?: string) {
    if (client === "ATT_IBC") {
        //for ATT_IBC calls, only in the case where the customer is a non-subscriber will we ever sort PA1 to be the first element returned from eligibility.
        const firstProduct = products.at(0);
        if (firstProduct?.productSku === "ATT_PA1_NOT_WITH_AAL") {
            return productSku === "ATT_PA1_NOT_WITH_AAL";
        }
    }
}

export function shouldShowNoOfferButton(params: { partner: string; selectedProduct?: EligibleProduct }): boolean {
    const { partner, selectedProduct } = params;
    return !selectedProduct && partner === "youtubetv";
}
